import { iconAlertaErro, iconAtendCelular, iconNavEmail, iconNavRelogio, iconSetaDireita, iconSetaDuplaEsquerda } from '@bsds-lib-component/icons/icons';
import { BsdsCard, BsdsIcon } from '@bsds-lib-component/react';
import React, { useCallback, useId, useRef, useState } from 'react';

import { Button, PhoneCodeInput, SystemMessage } from '../..';
import { useLogin } from '../../../hooks/useLogin';
import type { Contato } from '../../../types/apiType';
import type { LoginSecondStepFormProps, VerificationMethod } from '../../../types/componentType';
import { isCelular } from '../../../utils/phoneUtils';

/** Mask: first 3 + *** + last 3 (mobile or email). */
function maskContactValue(value: string | null | undefined): string {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  if (s.length <= 6) return s.length > 3 ? `${s.slice(0, 3)}***` : s;
  return `${s.slice(0, 3)}***${s.slice(-3)}`;
}

const LoginSecondStepForm: React.FC<LoginSecondStepFormProps> = ({
  onNext,
  onError,
  onLoadingChange,
  onBack,
  userId,
  maskedEmail: propMaskedEmail,
  maskedCellphone: propMaskedCellphone,
  secondStepApi: propSecondStepApi
}) => {
  const [selectedMethod, setSelectedMethod] = useState<VerificationMethod | null>(null);
  const [isCodeComplete, setIsCodeComplete] = useState(false);
  const [showContactSelection, setShowContactSelection] = useState(false);
  const [availableContacts, setAvailableContacts] = useState<Contato[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contato | null>(null);
  const mainContentId = useId();
  const methodSelectionId = useId();
  const codeInputId = useId();
  const timerId = useId();
  const errorMessageId = useId();
  const expiredMessageId = useId();
  const emailCardId = useId();
  const phoneCardId = useId();
  const validateButtonContainerRef = useRef<HTMLDivElement>(null);

  const hookApi = useLogin({
    onSuccess: onNext,
    onFormClear: () => { },
  }, userId);

  // Use parent's API when provided: same useLogin instance where verifyUser was called
  // and idPessoa/contacts are already filled (prevents "idPessoa not found" in 2nd step)
  const api = propSecondStepApi ?? {
    isLoading: hookApi.isLoading,
    error: hookApi.error,
    timeLeft: hookApi.timeLeft,
    isCodeExpired: hookApi.isCodeExpired,
    verificationCode: hookApi.verificationCode,
    showCodeFields: hookApi.showCodeFields,
    sendCodeToContact: hookApi.sendCodeToContact,
    fetchContacts: hookApi.fetchContacts,
    validateCode: hookApi.validateCode,
    updateCode: hookApi.updateCode,
    clearError: hookApi.clearError,
    resetVerificationState: hookApi.resetVerificationState,
    resetState: hookApi.resetState,
    contatos: hookApi.contatos,
    contatosRequestFailed: hookApi.contatosRequestFailed
  };

  const {
    isLoading,
    error,
    timeLeft,
    isCodeExpired,
    verificationCode,
    showCodeFields,
    sendCodeToContact,
    fetchContacts,
    validateCode,
    updateCode,
    clearError,
    resetVerificationState,
    resetState,
    contatos,
    contatosRequestFailed = false
  } = api;

  const maskedEmail = propMaskedEmail;
  const maskedCellphone = propMaskedCellphone;

  const hasEmail = Boolean(maskedEmail && maskedEmail.trim() !== '');
  const hasSms = Boolean(maskedCellphone && maskedCellphone.trim() !== '');
  const hasNoContacts = !showCodeFields && !showContactSelection && !hasEmail && !hasSms;

  // Notify parent about loading state changes
  React.useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading);
    }
  }, [isLoading, onLoadingChange]);

  // Notify parent about error changes
  React.useEffect(() => {
    if (onError && error) {
      onError(error);
    }
  }, [error, onError]);

  // Reset code completion when verification code changes
  React.useEffect(() => {
    const isComplete = verificationCode.every(digit => digit !== '');
    setIsCodeComplete(isComplete);
  }, [verificationCode]);

  const handleMethodChange = async (method: VerificationMethod) => {
    setSelectedMethod(method);
    resetVerificationState();
    clearError();
    setShowContactSelection(false);
    setAvailableContacts([]);
    setSelectedContact(null);

    try {
      // Search for contacts if they haven't been loaded yet
      let contactsList = contatos;
      if (contactsList.length === 0) {
        contactsList = await fetchContacts();
      }

      // Filter contacts by the selected type (phone: only mobile for SMS)
      const filteredContacts = method === 'email'
        ? contactsList.filter(c => c.email !== null)
        : contactsList.filter(c => c.telefone !== null && isCelular(c.telefone));

      if (filteredContacts.length === 0) {
        clearError();
        // Error will be set by the hook if necessary
        return;
      }

      // Always show the contact list for selection, even if there's only one contact
      setAvailableContacts(filteredContacts);
      setShowContactSelection(true);
    } catch {
      // Error is already handled by the useLogin hook if necessary
    }
  };

  const handleContactSelect = async (contact: Contato) => {
    setSelectedContact(contact);
    setShowContactSelection(false);
    clearError();

    try {
      await sendCodeToContact(contact);
    } catch {
      // Error is already handled by the useLogin hook
    }
  };

  const handleCodeChange = (value: string) => {
    // Convert string to array for the hook
    const codeArray = value.split('').concat(Array(6 - value.length).fill(''));
    codeArray.forEach((digit, index) => {
      updateCode(index, digit);
    });

    // Check if code is complete
    const isComplete = value.length === 6;
    setIsCodeComplete(isComplete);
  };


  const handleValidateCode = useCallback(async () => {
    try {
      await validateCode(verificationCode);
    } catch {
      // Error is already handled by the useLogin hook
    }
  }, [validateCode, verificationCode]);

  const canValidateCode = isCodeComplete && !isLoading && timeLeft > 0 && !isCodeExpired;

  const focusValidateButton = useCallback(() => {
    requestAnimationFrame(() => {
      validateButtonContainerRef.current?.querySelector('button')?.focus();
    });
  }, []);

  const handleCodeEnter = useCallback(() => {
    if (canValidateCode) {
      void handleValidateCode();
    }
  }, [canValidateCode, handleValidateCode]);

  React.useEffect(() => {
    if (!showCodeFields) return;

    const button = validateButtonContainerRef.current?.querySelector('button');
    if (!button) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && canValidateCode) {
        e.preventDefault();
        void handleValidateCode();
      }
    };

    button.addEventListener('keydown', onKeyDown);
    return () => button.removeEventListener('keydown', onKeyDown);
  }, [showCodeFields, canValidateCode, handleValidateCode]);

  const handleResendCode = async () => {
    if (!selectedContact) return;

    try {
      await sendCodeToContact(selectedContact);
    } catch {
      // Error is already handled by the useLogin hook
    }
  };

  const handleBack = () => {
    // If on code step, go back to contact selection or method selection
    if (showCodeFields) {
      resetVerificationState();
      setSelectedContact(null);
      setIsCodeComplete(false);
      clearError();
      if (showContactSelection || availableContacts.length > 1) {
        setShowContactSelection(true);
      } else {
        setShowContactSelection(false);
        setSelectedMethod(null);
        setAvailableContacts([]);
      }
    } else if (showContactSelection) {
      // If on contact selection, go back to method selection
      setShowContactSelection(false);
      setAvailableContacts([]);
      setSelectedContact(null);
      setSelectedMethod(null);
      clearError();
    } else {
      // If on method selection, go back to login screen
      resetState();
      setSelectedMethod(null);
      setIsCodeComplete(false);
      setShowContactSelection(false);
      setAvailableContacts([]);
      setSelectedContact(null);
      clearError();

      if (onBack) {
        onBack();
      }
    }
  };

  return (
    <div
      id={mainContentId}
      className=""
      role="main"
      aria-label="Segunda etapa de login - verificação de identidade"
      aria-busy={isLoading}
    >
      <div className="bsds-p-12">
        <div className="bsds-display-flex bsds-align-items-center space-x-2 cursor-pointer " onClick={handleBack}>
          <BsdsIcon icon={iconSetaDuplaEsquerda} className="text-gray-600 w-5 h-5" aria-hidden="true" />
          <h6>Voltar</h6>
        </div>
        <BsdsCard
          className="space-y-2 shadow-none rounded-md"
          role="region"
          aria-label="Formulário de verificação de identidade"
        >
          {!showCodeFields && !hasNoContacts && (
            <div className="bsds-text-start bsds-mb-24 bsds-mt-48 bsds-width-100">
              <div className="bsds-display-flex bsds-align-items-center bsds-justify-content-start bsds-mb-8">
                <div className="w-[2.5px] h-10 bg-red-600 bsds-mr-12" aria-hidden="true" />
                <h2 className="bsds-mb-0 bsds-mt-0 leading-none" style={{ fontSize: '32px' }}>
                  Autenticação <span className="font-bold">Adicional</span>
                </h2>
              </div>
              <h6 className="bsds-text-sm" style={{ fontSize: '12px', fontWeight: 400 }}>
                A verificação em duas etapas está ativa na sua conta.
              </h6>
              <h6 className="bsds-text-sm text-justify" style={{ fontSize: '12px', fontWeight: 400 }}>
               Para garantir que tudo fique seguro, escolha uma das opções abaixo para confirmar sua identidade.
              </h6>
            </div>
          )}

          {/* No email or SMS: message and back to login */}
          {hasNoContacts && (
            <div className="space-y-6 bsds-mt-16 bsds-flex-col bsds-align-items-center bsds-width-100" role="alert">
              <SystemMessage message="Não foi possível realizar o login" type="error" />
              <p className="text-sm text-gray-600 bsds-text-center">
                Nenhum e-mail ou número de celular encontrado para verificação de segurança. Entre em contato com o suporte.
              </p>
              {onBack && (
                <Button color="primary" type="button" onClick={onBack} className="bsds-mt-8">
                  Voltar ao login
                </Button>
              )}
            </div>
          )}

          {/* Method Selection - only when there is at least one contact (email or SMS) */} 
          {!showCodeFields && !showContactSelection && (hasEmail || hasSms) && (
            <div
              id={methodSelectionId}
              className="space-y-6 bsds-mt-16  bsds-flex-col bsds-align-items-center bsds-width-100"
              role="group"
              aria-label="Escolha o método de verificação"
            >
              <h5 className="text-sm font-bold bsds-text-center bsds-width-100">
                Selecione um dos meios de autenticação:
              </h5>
              {/* Email Card - only method selection, no email display - Show only if maskedEmail is not empty */}
              {maskedEmail && maskedEmail.trim() !== '' && (
                <div
                  id={emailCardId}
                  className="bsds-p-12 rounded-xl cursor-pointer transition-all duration-200 bsds-width-100 shadow-md max-w-md border border-gray-200  bg-white hover:border-primary-100 hover:bg-red-50/20"
                  onClick={() => handleMethodChange('email')}
                  role="button"
                  tabIndex={0}
                  aria-label="Verificação por e-mail"
                  aria-pressed={selectedMethod === 'email'}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleMethodChange('email');
                    }

                  }}
                >
                  <div className="bsds-display-flex bsds-align-items-center bsds-justify-content-between px-2">
                    <div className="bsds-display-flex bsds-align-items-center space-x-4">
                      <BsdsCard className={`bsds-p-4 rounded-xs  w-8 h-8 bsds-display-flex bsds-justify-content-center bsds-align-items-center border-gray-100/40 bg-gray-100/30`}
                        role="img"
                        aria-label="Ícone de e-mail"
                      >
                        <BsdsIcon icon={iconNavEmail} className="text-gray-600 w-5 h-5" aria-hidden="true" />
                      </BsdsCard>
                      <span className="text-sm font-semibold whitespace-nowrap">
                        E-mail Registrado
                      </span>
                    </div>
                    <BsdsIcon icon={iconSetaDireita} className="text-gray-600 w-5 h-5" aria-hidden="true" />
                  </div>
                </div>
              )}

              {/* Phone Card - only method selection, no phone display - Show only if maskedCellphone is not empty */}
              {maskedCellphone && maskedCellphone.trim() !== '' && (
                <div
                  id={phoneCardId}
                  className="bsds-p-12 rounded-xl cursor-pointer transition-all duration-200 bsds-width-100 shadow-md max-w-md border border-gray-200 bg-white hover:border-primary-100 hover:bg-red-50/20"
                  onClick={() => handleMethodChange('phone')}
                  role="button"
                  tabIndex={0}
                  aria-label="Verificação por SMS"
                  aria-pressed={selectedMethod === 'phone'}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleMethodChange('phone');
                    }
                  }}
                >
                  <div className="bsds-display-flex bsds-align-items-center bsds-justify-content-between px-2">
                    <div className="bsds-display-flex bsds-align-items-center space-x-4">
                      <BsdsCard className={`bsds-p-4 rounded-xs shadow-md w-8 h-8 bsds-display-flex bsds-justify-content-center bsds-align-items-center border-gray-100/40 bg-gray-100/30 `}
                        role="img"
                        aria-label="Ícone de celular"
                      >
                        <BsdsIcon icon={iconAtendCelular}
                          className="text-gray-600 w-5 h-5"
                          aria-hidden="true"
                        />
                      </BsdsCard>
                      <span className="text-sm font-semibold whitespace-nowrap">
                        SMS (Número Registrado)
                      </span>
                    </div>
                    <BsdsIcon icon={iconSetaDireita} className="text-gray-600 w-5 h-5" aria-hidden="true" />
                  </div>
                </div>
              )}
              <h6 />
            </div>
          )}

          {/* Contact Selection - list with scroll (max. 3 items visible at a time) */}
          {showContactSelection && !showCodeFields && (
            <div className="space-y-4 bsds-mt-16 bsds-flex-col bsds-align-items-center bsds-width-100">
              <h5 className="text-sm font-bold bsds-text-center bsds-width-100">
                Selecione o {selectedMethod === 'email' ? 'e-mail' : 'telefone'} para receber o código
              </h5>
              <div
                className="bsds-width-100 max-w-md space-y-4 overflow-y-auto pr-1"
                style={{ maxHeight: '20rem' }}
                role="list"
                aria-label={`Lista de ${selectedMethod === 'email' ? 'e-mails' : 'telefones'} registrados`}
              >
                {availableContacts.map((contact) => (
                  <div
                    key={`${contact.idContatoPessoa}-${contact.email ?? contact.telefone ?? ''}`}
                    className="bsds-p-12 rounded-xl cursor-pointer transition-all duration-200 bsds-width-100 shadow-md border border-gray-200 bg-white hover:border-primary-100 hover:bg-red-50/20"
                    onClick={() => handleContactSelect(contact)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Selecionar ${selectedMethod === 'email' ? 'e-mail' : 'telefone'}: ${selectedMethod === 'email' ? contact.email : contact.telefone}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleContactSelect(contact);
                      }
                    }}
                  >
                    <div className="bsds-display-flex bsds-align-items-center bsds-justify-content-between px-2">
                      <div className="bsds-display-flex bsds-align-items-center space-x-4">
                        <BsdsCard
                          className={`bsds-p-4 rounded-xs w-8 h-8 bsds-display-flex bsds-justify-content-center bsds-align-items-center border-gray-100/40 bg-gray-100/30`}
                          role="img"
                          aria-label={`Ícone de ${selectedMethod === 'email' ? 'e-mail' : 'celular'}`}
                        >
                          <BsdsIcon
                            icon={selectedMethod === 'email' ? iconNavEmail : iconAtendCelular}
                            className="text-gray-600 w-5 h-5"
                            aria-hidden="true"
                          />
                        </BsdsCard>
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-semibold truncate">
                            {selectedMethod === 'email'
                              ? maskContactValue(contact.email ?? '')
                              : maskContactValue(contact.telefone ?? '')}
                          </span>
                          {contact.dataInclusao && (
                            <span className="text-xs text-gray-600 bsds-mt-4">
                              Cadastrado em {contact.dataInclusao}
                            </span>
                          )}
                        </div>
                      </div>
                      <BsdsIcon icon={iconSetaDireita} className="text-gray-600 w-5 h-5 " aria-hidden="true" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </BsdsCard>

        {/* Error display */}
        {error && (
          <div
            id={errorMessageId}
            className="bsds-mt-8 bsds-display-flex bsds-flex-col bsds-align-items-center bsds-text-center"
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            <SystemMessage
              message={error}
              type="error"
            />
            {contatosRequestFailed && (
              <div className="bsds-mt-16 bsds-display-flex bsds-flex-col bsds-align-items-center bsds-gap-8">
                <p className="text-sm text-gray-600 bsds-text-center">
                  Não foi possível carregar os contatos, clique no botão abaixo para tentar novamente.
                </p>
                <Button
                  type="button"
                  color="primary"
                  fill="outline"
                  disabled={isLoading}
                  onClick={async () => {
                    clearError();
                    try {
                      await fetchContacts();
                    } catch {
                      // Error already set by the hook
                    }
                  }}
                  aria-label="Tentar listar contatos novamente"
                >
                  Tente novamente
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Verification code fields (6 digits) */}
        {showCodeFields && (
          <div className="button-container">
            <BsdsCard
              className="bsds-display-flex bsds-flex-col rounded-md shadow-none bsds-p-4 space-y-2 bsds-mt-16"
              role="region"
              aria-label="Validação do código de verificação"
            >
              <div className="bsds-display-flex bsds-justify-content-center bsds-align-items-center bsds-mb-8 bsds-mt-48" />
              <div>
                <div className="bsds-display-flex bsds-align-items-center space-x-2">
                  {selectedMethod === 'email' ? (
                    <div className="bsds-display-flex bsds-align-items-center space-x-4">
                      <BsdsCard className="bsds-p-4 rounded-xs shadow-md w-8 h-8 bsds-display-flex bsds-justify-content-center bsds-align-items-center border-gray-100/40 bg-gray-100/30"
                        role="img"
                        aria-label="Ícone de e-mail"
                      >
                        <BsdsIcon icon={iconNavEmail}
                          className="text-gray-600 w-5 h-5"
                          aria-hidden="true"
                        />
                      </BsdsCard>
                    </div>
                  ) : (
                    <div className="bsds-display-flex bsds-align-items-center space-x-4">
                      <BsdsCard className="bsds-p-4 rounded-xs shadow-md w-8 h-8 bsds-display-flex bsds-justify-content-center bsds-align-items-center border-gray-100/40 bg-gray-100/30"
                        role="img"
                        aria-label="Ícone de celular"
                      >
                        <BsdsIcon icon={iconAtendCelular}
                          className="text-gray-600 w-5 h-5"
                          aria-hidden="true"
                        />
                      </BsdsCard>
                    </div>
                  )}
                  <h3 className="text-xs font-bold ">Validar Código</h3>
                </div>
              </div>
              <h6 className="text-text-color text-xs font-normal">Código de verificação por {selectedMethod === 'email' ? 'e-mail' : 'SMS'}</h6>
              <h6 className="text-text-color text-xs font-normal">Insira o código de 6 dígitos que você recebeu</h6>
              <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center bsds-width-100 bsds-mt-16 bsds-mb-16">
                <label htmlFor={codeInputId} className="text-md font-medium text-text-color">
                  Enviamos um código de 6 dígitos para:
                </label>

              </div>
              {!isCodeExpired && (
                <>
                  <PhoneCodeInput
                    value={verificationCode.join('')}
                    onChange={handleCodeChange}
                    onComplete={focusValidateButton}
                    onEnter={handleCodeEnter}
                    placeholder="0"
                    required
                    autoFocus
                    aria-label="Código de verificação (obrigatório)"
                    aria-describedby={`${codeInputId}-help ${timerId}`}
                    aria-required
                  />
                  <span
                    id={`${codeInputId}-help`}
                    className="absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0"
                    style={{ clip: 'rect(0, 0, 0, 0)', clipPath: 'inset(50%)' }}
                  >
                    Digite o código de 6 dígitos recebido por {selectedMethod === 'email' ? 'e-mail' : 'SMS'}
                  </span>
                  <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center bsds-width-100 bsds-mt-16 bsds-mb-16">
                    <div className="bsds-display-flex bsds-align-items-center space-x-2">
                      {selectedMethod === 'email' ? (
                        <div className="bsds-display-flex bsds-align-items-center space-x-4">
                          <BsdsCard className="bsds-p-4 rounded-xs shadow-md w-8 h-8 bsds-display-flex bsds-justify-content-center bsds-align-items-center border-gray-100/40 bg-gray-100/30"
                            role="img"
                            aria-label="Ícone de e-mail"
                          >
                            <BsdsIcon icon={iconNavEmail}
                              className="text-gray-600 w-5 h-5"
                              aria-hidden="true"
                            />
                          </BsdsCard>
                        </div>
                      ) : (
                        <div className="bsds-display-flex bsds-align-items-center space-x-4">
                          <BsdsCard className="bsds-p-4 rounded-xs shadow-md w-8 h-8 bsds-display-flex bsds-justify-content-center bsds-align-items-center border-gray-100/40 bg-gray-100/30"
                            role="img"
                            aria-label="Ícone de celular"
                          >
                            <BsdsIcon icon={iconAtendCelular}
                              className="text-gray-600 w-5 h-5"
                              aria-hidden="true"
                            />
                          </BsdsCard>
                        </div>
                      )}
                      <h6 className="text-text-color text-xs font-normal">
                        {selectedContact
                          ? (selectedMethod === 'email'
                            ? maskContactValue(selectedContact.email ?? '')
                            : maskContactValue(selectedContact.telefone ?? ''))
                          : (selectedMethod === 'email'
                            ? maskContactValue(maskedEmail ?? '')
                            : maskContactValue(maskedCellphone ?? ''))}
                      </h6>
                    </div>
                    {timeLeft > 0 ? (
                      <div
                        id={timerId}
                        className="bsds-display-flex bsds-align-items-center bsds-justify-content-center space-x-1 rounded-3xl bsds-p-2 bg-secondary-100/50 w-[60px] text-secondary-500 text-sm"
                        role="timer"
                        aria-live="polite"
                        aria-label={`Tempo restante: ${timeLeft} segundos`}
                      >
                        <BsdsIcon className="bsds-width-28 bsds-height-28 mb-4 mr-1" icon={iconNavRelogio} aria-hidden="true" />
                        0:{timeLeft < 10 ? `0${timeLeft}` : timeLeft}
                      </div>
                    ) : (
                      <div className="bsds-display-flex bsds-justify-content-center bsds-align-items-center space-x-2">

                        <button
                          type="button"
                          className="bsds-text-center text-[12px] cursor-pointer transition-colors font-semibold bg-transparent border-none p-0"
                          onClick={handleResendCode}
                          disabled={isLoading || timeLeft > 0}
                          aria-label={
                            timeLeft > 0
                              ? `Aguarde ${timeLeft} segundos para reenviar o código`
                              : `Reenviar código por ${selectedMethod === 'email' ? 'e-mail' : 'SMS'}`
                          }
                          aria-busy={isLoading}
                        >
                          Reenviar {selectedMethod === 'email' ? 'E-mail' : 'SMS'}
                        </button>
                      </div>
                    )}
                  </div>


                  {/* Navigation buttons */}
                  <div className="bsds-width-100" ref={validateButtonContainerRef}>
                    <div className="bsds-flex-1" />
                    <Button
                      color="primary"
                      type="button"
                      onClick={handleValidateCode}
                      className="bsds-width-100 bsds-mt-16"
                      disabled={!canValidateCode}
                      aria-label={
                        isLoading
                          ? 'Validando código...'
                          : !isCodeComplete
                            ? 'Preencha o código de 6 dígitos para validar'
                            : timeLeft === 0 || isCodeExpired
                              ? 'Código expirado. Reenvie um novo código'
                              : 'Validar código de verificação'
                      }
                      aria-busy={isLoading}
                    >
                      Validar Código
                    </Button>

                    {/* Expired code message */}
                    {timeLeft === 0 && (
                      <div
                        id={expiredMessageId}
                        className="border-red-300 bg-red-100 rounded-md bsds-p-8 bsds-mt-8"
                        role="alert"
                        aria-live="assertive"
                        aria-label="Código expirado"
                      >
                        <BsdsIcon className="bsds-width-4 bsds-height-4 -600 mb-3 mr-2 text-primary-600" icon={iconAlertaErro} aria-hidden="true" />
                        <span className="bsds-text-center text-sm text-white font-semibold">
                          Código expirado! Clique em "Reenviar {selectedMethod === 'email' ? 'E-mail' : 'SMS'}" para receber um novo código.
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}
              <div className="text-text-color/60 text-[10px] font-semibold bsds-display-flex bsds-flex-wrap bsds-align-items-center bsds-justify-content-center bsds-text-center gap-x-1">
                <span>Não recebeu o código? Verifique sua caixa de spam </span>
                {timeLeft === 0 && (
                  <>
                    <span>ou </span>
                    <button
                      type="button"
                      className="inline bsds-text-sm bsds-text-primary cursor-pointer underline bg-transparent border-none p-0 font-semibold align-baseline"
                      onClick={() => {
                        const alternate: VerificationMethod = selectedMethod === 'email' ? 'phone' : 'email';
                        void handleMethodChange(alternate);
                      }}
                      aria-label={`Tentar receber o código por ${selectedMethod === 'email' ? 'SMS' : 'e-mail'}`}
                    >
                      tente por {selectedMethod === 'email' ? 'SMS' : 'E-mail'}
                    </button>
                  </>
                )}
              </div>
            </BsdsCard>

          </div>
        )}
      </div>
      <h6 className={`text-text-color/60 text-[10px] font-bold bsds-text-center  ${!showCodeFields && !showContactSelection ? 'mt-16' : showContactSelection ? 'mt-0' : timeLeft > 0 ? 'mt-20' : 'mt-0'}`}>
        Essa verificação adicional protege a sua conta contra acessos não autorizados
      </h6>
    </div>
  );
};

export default LoginSecondStepForm;